import * as React from 'react';
import LocalDiv from '../localization/LocalDiv';
import LocalInput from '../localization/LocalInput';
import { Str } from '../localization/translationKeys';
import ActionIcon from './ActionIcon';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { css } from '@emotion/core';
import { COLORS } from '../globalStyle/colors';
import { INPUT } from '../forms/styles/input';
import { FIELDSET_DEFAULT } from './fieldSetStyle';
import { FLEX_COLUMN } from './flexBox';
import { useMemo, useState } from 'react';
import { isNilOrEmpty } from '../language/module';


const styles = {
  root:       css`background-color: ${COLORS.WHITE};`,
  inputRow:   {
    root:  css`padding: 12px;`,
    input: [
      INPUT(COLORS.LIGHT_GREY),
      css`
        min-width: 300px;
      `,
    ],
  },
  searchIcon: {
    root: css`
      transform: translateX(-24px);
    `,
    icon: css`
      color: ${COLORS.LIGHT_GREY};
    `,
  },
  dataRows:   {
    root: [
      FLEX_COLUMN,
      css`
      background-color: ${COLORS.WHITE};
      max-height: 224px;
      overflow-y: auto;
    `,
    ],
    row:  css`
      outline: none;
      border: 0 none transparent;
      border-top: 1px solid ${COLORS.LIGHTER_GREY};
      padding: 20px 12px;
      text-align: left;
      font-size: 12px;
    `,
  },
};


type ModelViewData = {
  key: string;
  displayValue: string | number;
}


type Props = {
  className?: string;

  dataValues: ModelViewData[];

  isChecked: (key: string) => boolean;
  toggleChecked: (key: string) => void;
  isProject: boolean;
}


/**
 * SearchableDataModelList: a component that renders a collection of data models as a simple list, as well as a search
 * bar which enables easy filtering of the list.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const SearchableDataModelList: React.FC<Props> = props => {
  const { isChecked, toggleChecked, ...rest } = props;

  const [currentText, setCurrentText] = useState<string>('');;
  const [searchText, setSearchText] = useState<string>('');;

  const searchData = useMemo(
    () => (
      isNilOrEmpty(searchText)
      ? rest.dataValues
      : rest.dataValues.filter(d => d.displayValue.toString().toLowerCase().search(searchText.toLowerCase()) !== -1)
    ),
    [rest.dataValues, searchText],
  );

  const options = useMemo(
    () => searchData.map(
      model => {
        const checked = isChecked(model.key);

        return (
          <button
            aria-checked={checked}
            role='checkbox'
            key={model.key}
            value={model.key}
            css={[styles.dataRows.row, css`background-color: ${checked ? COLORS.LIGHTEST_GREY : COLORS.WHITE};`]}
            onClick={() => toggleChecked(model.key)}
          >
            {model.displayValue}
          </button>
        );
      },
    ),
    [isChecked, searchData, toggleChecked],
  );

  const onSearchClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    setSearchText(currentText);
  };

  const searchStr = useMemo(
    () => props.isProject ? Str.SearchProjects : Str.SearchParticipants,
    [props],
  );

  return (
    <LocalDiv css={styles.root} className={rest.className}>
      {
        options.length > 15 && (
          <LocalDiv css={styles.inputRow.root}>
            <fieldset css={FIELDSET_DEFAULT}>
              <form>
                <LocalInput
                  name='searchText'
                  placeholderTranslation={searchStr}
                  css={styles.inputRow.input}
                  onChange={event => setCurrentText(event.target.value)}
                />
                <ActionIcon
                  icon={faSearch}
                  iconStyle={styles.searchIcon.icon}
                  css={styles.searchIcon.root}
                  onClick={onSearchClick}
                />
              </form>
            </fieldset>
          </LocalDiv>
        )
      }
      <LocalDiv css={styles.dataRows.root}>
        {options}
      </LocalDiv>
    </LocalDiv>
  );
};

export default SearchableDataModelList;
